import { PhasrMonitoredRulesDetailsPanelComponent } from '../component/PhasrMonitoredRulesDetailsPanelComponent';
import { PhasrMonitoredRulesDetailsPanelView } from '../view/PhasrMonitoredRulesDetailsPanelView';
import { templatePhasrMonitoredRulesDetailsPanel } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { translateFn } from '../../../utils/translator.helper';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { SideMenuWidthValues } from '../../../ui/gzSideMenu/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { PhasrSidePanelComponent } from "../../../phasrSidePanel";
import { PhasrPanelComponentTypes, PhasrSidePanelEvents } from '../../../phasrSidePanel/types/types';
import { ColumnKeys } from '../../../phasrRecommendations/phasrRecommendationsGrid/types/grid';
import { PhasrMonitoredRulesDetailsPanelRepository } from '../repository/PhasrMonitoredRulesDetailsPanelRepository';
import { GeneralMonitoredRule, INCIDENT_DISPLAY_LIMIT, IncidentData } from '../types';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { addCloseSideMenuFunctionality } from '../../common/helpers';
import { PhasrApplyActionSchema } from '../../../phasrApplyActionPanel/types';
import { PhasrMonitoredRulesGridEvents } from '../../phasrMonitoredRulesGrid/types';
import {
  getPhasrCategoryDictionaryToPrefferedName,
} from '../../../types/common/phasrTypes';
import { ALLOW_ACTION, RESTRICT_ACTION } from '../../../phasrRecommendations/phasrRecommendationsGrid/types';
import { isEmpty } from 'lodash';

export class PhasrMonitoredRulesDetailsPanelController extends BaseWebComponentController {
  public component: PhasrMonitoredRulesDetailsPanelComponent;
  public view: PhasrMonitoredRulesDetailsPanelView;
  public dataRepository: PhasrMonitoredRulesDetailsPanelRepository;
  private page = 1;

  constructor(component: PhasrMonitoredRulesDetailsPanelComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: PhasrMonitoredRulesDetailsPanelView,
      viewTemplate: templatePhasrMonitoredRulesDetailsPanel,
      dataRepositoryConstructor: PhasrMonitoredRulesDetailsPanelRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    const eventListeners = [
      {
        element: this.view.dom.$behavioralProfilesWrapper,
        event: 'click',
        callback: () => this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.BehavioralProfiles, this.component.generalDetails)
      },
      {
        element: this.view.dom.$restrictedBehavioralProfilesWrapper,
        event: 'click',
        callback: () => this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.RestrictedBehavioralProfiles, this.component.generalDetails)
      },
      {
        element: this.view.dom.$incidentsInvolvingRuleTriggersShowMore,
        event: 'click',
        callback: () => this.handleIncidentsInvolvingRuleTriggersShowMore(),
      },
      {
        element: this.view.dom.$accessButton,
        event: 'click',
        callback: () => this.openActionPanel(),
      }
    ] as IListenerConfig[];
    if (this.component.generalDetails.recommendations !== 0) {
      this.view.dom.$recommendations.style.cursor = 'pointer';
      this.view.dom.$recommendations.style.color = 'var(--blue-layout-400-primary)';
      eventListeners.push({
        element: this.view.dom.$recommendations,
        event: 'click',
        callback: () => this.handleRecommendationsClickEvent(this.component.generalDetails),
      },)
    }
    if (this.component.generalDetails.ruleTriggers !== 0) {
      this.view.dom.$ruleTrigger.style.cursor = 'pointer';
      this.view.dom.$ruleTrigger.style.color = 'var(--blue-layout-400-primary)';
      eventListeners.push({
        element: this.view.dom.$ruleTrigger,
        event: 'click',
        callback: () => this.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.RuleTriggers, this.component.generalDetails)
      });
    }
    return eventListeners;
  }

  async getDataForView() {
    this.component.generalDetails.incidentsInvolvingRuleTriggers = await this.getIncidentsInvolvingRuleTriggers();
    this.view.dom.$loadingSpinner.classList.add('hidden');
    this.view.dom.$wrapper.classList.remove('hidden');
    return {
      ruleName: this.component.ruleName,
      generalDetails: this.component.generalDetails,
      monitoredRuleDescription: this.component.monitoredRuleDescription,
    };
  }

  private getBehavioralProfilesPanel(data: GeneralMonitoredRule): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Behavioral profiles');
    sidePanelContent.componentType = PhasrPanelComponentTypes.BehavioralProfiles;
    sidePanelContent.hasBackButton = true;
    sidePanelContent.companyId = data.companyId;
    sidePanelContent.componentSpecificData = {
      recommendationId: data.recommendationId
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  private getRestrictedBehavioralProfilesPanel(data: GeneralMonitoredRule): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Restricted behavioral profiles');
    sidePanelContent.componentType = PhasrPanelComponentTypes.RestrictedBehavioralProfiles;
    sidePanelContent.hasBackButton = true;
    sidePanelContent.companyId = data?.companyId;
    sidePanelContent.componentSpecificData = {
      recommendationId: data?.recommendationId,
      ruleId: data?.ruleId,
      ruleTriggers: data?.ruleTriggers,
      phasrUsable: data?.phasrUsable,
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  private getRuleTriggersPanel(data: GeneralMonitoredRule): PhasrSidePanelComponent {
    const sidePanelContent = new PhasrSidePanelComponent();
    sidePanelContent.titleText = translateFn('Rule triggers');
    sidePanelContent.componentType = PhasrPanelComponentTypes.RuleTriggers;
    sidePanelContent.hasBackButton = true;
    sidePanelContent.companyId = data.companyId;
    sidePanelContent.componentSpecificData = {
      ruleId: data.ruleId
    }
    sidePanelContent.injectDependencies(this.dependencies);
    sidePanelContent.start().catch(console.error);
    return sidePanelContent;
  }

  private handlePhasrSidePanelOpen(phasrComponentType: PhasrPanelComponentTypes, data: GeneralMonitoredRule): void {
    const phasrSidePanel = new GzSideMenuWebComponent();
    phasrSidePanel.isOpen = true;
    phasrSidePanel.closeOnClickOutside = true;
    phasrSidePanel.menuWidth = SideMenuWidthValues.MEDIUM;
    phasrSidePanel.setAttribute('id', phasrComponentType);
    phasrSidePanel.classList.add('phasr-side-panel');
    let phasrSidePanelContent;
    switch (phasrComponentType) {
      case PhasrPanelComponentTypes.BehavioralProfiles:
        phasrSidePanelContent = this.getBehavioralProfilesPanel(data)
        break;
      case PhasrPanelComponentTypes.RestrictedBehavioralProfiles:
        phasrSidePanelContent = this.getRestrictedBehavioralProfilesPanel(data);
        break;
      case PhasrPanelComponentTypes.RuleTriggers:
        phasrSidePanelContent = this.getRuleTriggersPanel(data);
        break;
      default:
        break;

    }
    phasrSidePanel.setMenuContent(phasrSidePanelContent);
    phasrSidePanel.openSideMenu();
    phasrSidePanel.addEventListener('close', () => phasrSidePanel.remove());
    phasrSidePanel.addEventListener(PhasrSidePanelEvents.EditAccess, (e: any) => {
      this.component.dispatchEvent(new CustomEvent(PhasrSidePanelEvents.EditAccess, {detail: e.detail}));
    });

    addCloseSideMenuFunctionality(phasrSidePanel);
  }

  private handleRecommendationsClickEvent(data: GeneralMonitoredRule): void {
    const dictionary = Application.Main.getPhasrDictionary();
    const params: {
      filters: Partial<Record<ColumnKeys, unknown>>[];
    } = {
      filters: [
        {
          [ColumnKeys.COMPANY]: {
            exactMatch: data.companyId
          },
        },
        {
          [ColumnKeys.RECOMMENDATION_NAME]: {
            complexEnumFilter: [
              {
                value: data.ruleId + '-' + RESTRICT_ACTION,
                label: dictionary[this.component.generalDetails.ruleId]?.rule_recommendation ?? 'N/A',
              },
              {
                value: data.ruleId + '-' + ALLOW_ACTION,
                label: dictionary[this.component.generalDetails.ruleId]?.rule_allow_name ?? 'N/A',
              }
            ]
          },
        },
        {
          [ColumnKeys.CREATED_ON]: {
            timeRange: {
              kind: TimeRangeKind.ALL_TO_DATE,
            },
          }
        },
      ]
    };
    NavigationHelper.navigateTo('/phasr-recommendations', params);
  }

  private async handleIncidentsInvolvingRuleTriggersShowMore(): Promise<void> {
    this.page += 1;
    const newDataIncident = await this.getIncidentsInvolvingRuleTriggers();
    this.view.buildIncidentsInvolvingRuleTriggers(newDataIncident);
  }

  private async getIncidentsInvolvingRuleTriggers(): Promise<IncidentData[]> {
    const incidents = await this.dataRepository.getIncidentsByRuleId(this.page, this.component.generalDetails.ruleId, this.component.generalDetails.companyId);
    if (incidents.length === INCIDENT_DISPLAY_LIMIT) {
      this.view.moreIncidents = !isEmpty(await this.dataRepository.getIncidentsByRuleId(this.page + 1, this.component.generalDetails.ruleId, this.component.generalDetails.companyId));
    } else {
      this.view.moreIncidents = false;
    }
    return incidents;
  }

  private openActionPanel(): void {
    const dictionary = Application.Main.getPhasrDictionary();
    const data: PhasrApplyActionSchema = {
      id: this.component.generalDetails.recommendationId,
      companyId: this.component.generalDetails.companyId,
      ruleId: this.component.generalDetails.ruleId,
      description: dictionary[this.component.generalDetails.ruleId]?.rule_description ?? 'N/A',
      targetedActivityType: getPhasrCategoryDictionaryToPrefferedName()[dictionary[this.component.generalDetails.ruleId]?.['category']] ?? 'N/A',
      rule: this.component.generalDetails.ruleTriggers,
    };

    const event = new CustomEvent(PhasrMonitoredRulesGridEvents.OnEditAccessFromDetailsPanel, {
      detail: data,
      bubbles: true,
      composed: true,
    });

    this.component.dispatchEvent(event);
  }
}
